import { openDB, IDBPDatabase } from 'idb';

// Queue operation types
export type QueueOperationType = 'create-agreement' | 'post-payment' | 'add-note';

// Queue item interface
export interface QueueItem {
  id: string; // Client-generated UUID
  operationType: QueueOperationType;
  idempotencyKey: string;
  payload: any;
  metadata: {
    timestamp: number;
    retryCount: number;
    lastRetry?: number;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    error?: string;
  };
  createdAt: number;
  updatedAt: number;
}

// Database schema
interface OfflineQueueDB extends IDBPDatabase {
  // Queues for different operation types
  'create-agreement-queue': IDBObjectStore;
  'post-payment-queue': IDBObjectStore;
  'add-note-queue': IDBObjectStore;
  // Media uploads queue
  'media-uploads-queue': IDBObjectStore;
  // Sync status
  'sync-status': IDBObjectStore;
}

// Database configuration
const DB_NAME = 'kist-offline-queue';
const DB_VERSION = 1;

// Queue names
const QUEUE_NAMES = {
  'create-agreement': 'create-agreement-queue',
  'post-payment': 'post-payment-queue',
  'add-note': 'add-note-queue',
  'media-uploads': 'media-uploads-queue',
} as const;

// Initialize database
async function initDB(): Promise<IDBPDatabase<OfflineQueueDB>> {
  return openDB<OfflineQueueDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      // Create queues for each operation type
      Object.values(QUEUE_NAMES).forEach(queueName => {
        if (!db.objectStoreNames.contains(queueName)) {
          const store = db.createObjectStore(queueName, { keyPath: 'id' });
          // Index by idempotency key for deduplication
          store.createIndex('idempotencyKey', 'idempotencyKey', { unique: true });
          // Index by status for processing
          store.createIndex('status', 'metadata.status');
          // Index by timestamp for ordering
          store.createIndex('timestamp', 'createdAt');
        }
      });

      // Create sync status store
      if (!db.objectStoreNames.contains('sync-status')) {
        const syncStore = db.createObjectStore('sync-status', { keyPath: 'key' });
        syncStore.createIndex('key', 'key');
      }
    },
  });
}

// Generate UUID for queue items
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Generate idempotency key
function generateIdempotencyKey(operationType: QueueOperationType, payload: any): string {
  const payloadHash = btoa(JSON.stringify(payload)).slice(0, 16);
  return `${operationType}-${payloadHash}-${Date.now()}`;
}

// Offline Queue Service
export class OfflineQueueService {
  private db: IDBPDatabase<OfflineQueueDB> | null = null;

  async init(): Promise<void> {
    if (!this.db) {
      this.db = await initDB();
    }
  }

  // Add item to queue
  async addToQueue(
    operationType: QueueOperationType,
    payload: any,
    customIdempotencyKey?: string
  ): Promise<QueueItem> {
    await this.init();

    const id = generateUUID();
    const idempotencyKey = customIdempotencyKey || generateIdempotencyKey(operationType, payload);
    const timestamp = Date.now();

    const queueItem: QueueItem = {
      id,
      operationType,
      idempotencyKey,
      payload,
      metadata: {
        timestamp,
        retryCount: 0,
        status: 'pending',
      },
      createdAt: timestamp,
      updatedAt: timestamp,
    };

    try {
      // Check for existing item with same idempotency key
      const existing = await this.db!.getFromIndex(
        QUEUE_NAMES[operationType],
        'idempotencyKey',
        idempotencyKey
      );

      if (existing) {
        // Return existing item if found (idempotency)
        return existing;
      }

      // Add new item to queue
      await this.db!.add(QUEUE_NAMES[operationType], queueItem);
      return queueItem;
    } catch (error) {
      console.error('Error adding to queue:', error);
      throw error;
    }
  }

  // Get pending items from queue
  async getPendingItems(operationType: QueueOperationType): Promise<QueueItem[]> {
    await this.init();

    const items = await this.db!.getAllFromIndex(
      QUEUE_NAMES[operationType],
      'status',
      'pending'
    );

    // Sort by creation timestamp (oldest first)
    return items.sort((a, b) => a.createdAt - b.createdAt);
  }

  // Get all items from queue
  async getAllItems(operationType: QueueOperationType): Promise<QueueItem[]> {
    await this.init();

    const items = await this.db!.getAll(QUEUE_NAMES[operationType]);
    return items.sort((a, b) => a.createdAt - b.createdAt);
  }

  // Update item status
  async updateItemStatus(
    operationType: QueueOperationType,
    id: string,
    status: QueueItem['metadata']['status'],
    error?: string
  ): Promise<void> {
    await this.init();

    const item = await this.db!.get(QUEUE_NAMES[operationType], id);
    if (!item) {
      throw new Error(`Queue item not found: ${id}`);
    }

    item.metadata.status = status;
    item.metadata.updatedAt = Date.now();
    
    if (error) {
      item.metadata.error = error;
    }

    if (status === 'processing') {
      item.metadata.retryCount += 1;
      item.metadata.lastRetry = Date.now();
    }

    await this.db!.put(QUEUE_NAMES[operationType], item);
  }

  // Remove completed item from queue
  async removeItem(operationType: QueueOperationType, id: string): Promise<void> {
    await this.init();
    await this.db!.delete(QUEUE_NAMES[operationType], id);
  }

  // Get queue statistics
  async getQueueStats(): Promise<Record<QueueOperationType, { pending: number; total: number }>> {
    await this.init();

    const stats: Record<QueueOperationType, { pending: number; total: number }> = {
      'create-agreement': { pending: 0, total: 0 },
      'post-payment': { pending: 0, total: 0 },
      'add-note': { pending: 0, total: 0 },
    };

    for (const operationType of Object.keys(stats) as QueueOperationType[]) {
      const allItems = await this.getAllItems(operationType);
      const pendingItems = allItems.filter(item => item.metadata.status === 'pending');
      
      stats[operationType] = {
        pending: pendingItems.length,
        total: allItems.length,
      };
    }

    return stats;
  }

  // Update sync status
  async updateSyncStatus(lastSynced: number): Promise<void> {
    await this.init();
    
    await this.db!.put('sync-status', {
      key: 'lastSynced',
      value: lastSynced,
      updatedAt: Date.now(),
    });
  }

  // Get last sync timestamp
  async getLastSyncTimestamp(): Promise<number | null> {
    await this.init();
    
    const status = await this.db!.get('sync-status', 'lastSynced');
    return status?.value || null;
  }

  // Clear all queues (for testing/debugging)
  async clearAllQueues(): Promise<void> {
    await this.init();
    
    for (const queueName of Object.values(QUEUE_NAMES)) {
      await this.db!.clear(queueName);
    }
  }

  // Check if there are any pending items
  async hasPendingItems(): Promise<boolean> {
    const stats = await this.getQueueStats();
    return Object.values(stats).some(stat => stat.pending > 0);
  }
}

// Export singleton instance
export const offlineQueue = new OfflineQueueService(); 